import { Button, Icon } from "@resource/atlas";
import { atlasPlay } from "@resource/atlas/icons";
import clsx from "clsx";
import defaultAvatarImage from "icons/legacy/default-avatar.svg";
import prependHttp from "prepend-http";
import useAnalytics from "react-hooks/useAnalytics";
import { Interviewer as InterviewerType } from "types/legacy";
import iconForProfileUrl, {
  labelForProfileUrl,
} from "utils/icon-for-profile-url";

type InterviewerProfileProps = {
  onVideoIntroductionClick(): void;
  personalProfile: InterviewerType;
  variant: "small" | "regular";
};
function InterviewerProfile({
  onVideoIntroductionClick,
  personalProfile,
  variant,
}: InterviewerProfileProps) {
  const analytics = useAnalytics();

  const {
    firstName,
    lastName,
    imageUrl,
    title,
    biography: bio,
    websiteUrls,
    pronoun,
    videoIntroductionUrl,
  } = personalProfile;

  const profileUrlButtons = websiteUrls.map((url) => (
    <Button
      key={url}
      as="a"
      isGhost
      target="_blank"
      aria-label={labelForProfileUrl(url)}
      href={prependHttp(url)}
      negativeMargin="left"
      onClick={() => {
        analytics.track("Website url Clicked");
      }}
      icon={iconForProfileUrl(url)}
    />
  ));

  return (
    <div className="flex flex-col space-y-4 items-center sm:space-x-4 sm:flex-row sm:items-start sm:space-y-0">
      <div className="mt-0 flex-shrink-0">
        {videoIntroductionUrl ? (
          <button
            type="button"
            className={clsx(
              "pointer relative rounded-md bg-no-repeat bg-cover",
              {
                "bg-[#F2F5F7]": !imageUrl,
                "bg-black": !!imageUrl,
                "w-44": variant === "regular",
                "w-36": variant === "small",
              }
            )}
            onClick={onVideoIntroductionClick}
            style={{
              backgroundImage: `url(${imageUrl || defaultAvatarImage.src})`,
              aspectRatio: "1",
            }}
          >
            <div className="rounded-md absolute bottom-2 right-2 py-[.625rem] px-4 bg-red-500 flex space-x-2 text-white">
              <Icon content={atlasPlay} size="medium" />
              <span className="text-body-md-heavy">Intro</span>
            </div>
          </button>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            className={clsx("relative rounded-md aspect-square object-cover", {
              "bg-[#F2F5F7]": !imageUrl,
              "bg-black": !!imageUrl,
              "w-44": variant === "regular",
              "w-36": variant === "small",
            })}
            alt={`${firstName} ${lastName} avatar`}
            src={imageUrl || defaultAvatarImage.src}
          />
        )}
      </div>

      <div className="text-center space-y-4 sm:text-left">
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center space-x-2 sm:justify-start justify-center">
              <div
                className={clsx("mt-0", {
                  "text-h1": variant === "regular",
                  "text-h3": variant === "small",
                })}
              >{`${firstName} ${lastName}`}</div>
              <div className="flex">
                {variant === "small" && !!websiteUrls?.length && (
                  <>{profileUrlButtons}</>
                )}
              </div>
            </div>
            <p className="text-body-md text-subtle">{title}</p>
            {pronoun && <p className="text-body-md text-subtle">({pronoun})</p>}
          </div>
          {variant === "regular" && !!websiteUrls?.length && (
            <div className="flex justify-center sm:justify-start">
              {profileUrlButtons}
            </div>
          )}
        </div>
        {bio && (
          <>
            {variant === "regular" && (
              <div className="my-2 w-full h-[.0625rem] bg-light-gray-900" />
            )}
            <p className="text-body-md whitespace-pre-line" title={bio}>
              {bio}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default InterviewerProfile;
